import React, { useState } from 'react';
import { Database, Eye, Trash2, Calendar, Clock, Zap } from 'lucide-react';
import { QueryResult } from './types';
import { ResultViewer } from './ResultViewer';

interface ResultsGalleryProps {
    results: QueryResult[];
    selectedResults: Set<string>;
    onToggleResult: (queryId: string) => void;
    onDeleteResult: (queryId: string) => void;
}

export const ResultsGallery: React.FC<ResultsGalleryProps> = ({
    results,
    selectedResults,
    onToggleResult,
    onDeleteResult
}) => {
    const [viewingResult, setViewingResult] = useState<QueryResult | null>(null);

    const formatTimestamp = (timestamp: string) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 1) return 'Nettopp';
        if (diffMins < 60) return `${diffMins} min siden`;
        
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours} time${diffHours !== 1 ? 'r' : ''} siden`;
        
        return date.toLocaleDateString('nb-NO', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <>
            <div className="bg-white rounded-lg shadow h-full flex flex-col">
                {/* Header */}
                <div className="px-4 py-3 border-b bg-gray-50">
                    <h2 className="font-semibold text-gray-800">Resultater</h2>
                    <p className="text-xs text-gray-500 mt-1">
                        {results.length} resultat{results.length !== 1 ? 'er' : ''} totalt
                    </p>
                </div>

                {/* Results List */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {results.length === 0 && (
                        <div className="text-center py-12 text-gray-400">
                            <Database size={48} className="mx-auto mb-4 opacity-30" />
                            <p className="text-sm">Ingen resultater ennå</p>
                            <p className="text-xs mt-1">Dine spørringer vil vises her</p>
                        </div>
                    )}

                    {results.map(result => (
                        <div
                            key={result.queryId}
                            className={`border rounded-lg p-3 transition-all ${
                                selectedResults.has(result.queryId)
                                    ? 'border-purple-500 bg-purple-50'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                        >
                            {/* Checkbox and Query */}
                            <div className="flex items-start gap-2 mb-2">
                                <input
                                    type="checkbox"
                                    checked={selectedResults.has(result.queryId)}
                                    onChange={() => onToggleResult(result.queryId)}
                                    className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 rounded"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800 line-clamp-2">
                                        {result.query}
                                    </p>
                                </div>
                            </div>

                            {/* Status and Stats */}
                            {result.success ? (
                                <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                                    <div className="flex items-center gap-1">
                                        <Database size={12} />
                                        <span>{result.rowCount} rad{result.rowCount !== 1 ? 'er' : ''}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Clock size={12} />
                                        <span>{result.executionTimeMs}ms</span>
                                    </div>
                                    {result.tokensUsed && (
                                        <div className="flex items-center gap-1">
                                            <Zap size={12} />
                                            <span>{result.tokensUsed}</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="flex items-center gap-1 text-xs text-red-600 mb-2">
                                    <span>Feilet</span>
                                </div>
                            )}

                            {/* Preview - first 3 rows */}
                            {result.success && result.rows.length > 0 && (
                                <div className="text-xs bg-gray-50 rounded p-2 mb-2 overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="text-left border-b border-gray-200">
                                                {result.columns.slice(0, 3).map((col, idx) => (
                                                    <th key={idx} className="pb-1 pr-2 font-medium text-gray-700">
                                                        {col.displayName}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.rows.slice(0, 3).map((row, rowIdx) => (
                                                <tr key={rowIdx} className="border-b border-gray-100 last:border-0">
                                                    {result.columns.slice(0, 3).map((col, colIdx) => (
                                                        <td key={colIdx} className="py-1 pr-2 text-gray-600 truncate max-w-[100px]">
                                                            {row[col.name]?.toString() || '-'}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {result.rows.length > 3 && (
                                        <p className="text-gray-500 mt-1 text-center">
                                            +{result.rows.length - 3} flere rad{result.rows.length - 3 !== 1 ? 'er' : ''}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Timestamp */}
                            <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
                                <Calendar size={12} />
                                <span>{formatTimestamp(result.timestamp)}</span>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                                {result.success && (
                                    <button
                                        onClick={() => setViewingResult(result)}
                                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
                                    >
                                        <Eye size={14} />
                                        <span>Se</span>
                                    </button>
                                )}
                                <button
                                    onClick={() => onDeleteResult(result.queryId)}
                                    className="flex items-center justify-center gap-1 px-2 py-1.5 bg-red-50 text-red-600 rounded text-xs font-medium hover:bg-red-100 transition-colors"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Result Viewer Modal */}
            {viewingResult && (
                <ResultViewer 
                    result={viewingResult}
                    onClose={() => setViewingResult(null)}
                />
            )}
        </>
    );
};
